import React, { useState, useRef } from "react";
import {
  Typography,
  Card,
  CardContent,
  Box,
  Button,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import {
  Warning as WarningIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  Check as CheckIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { apiRequest } from "../config/api";
import { useSeason } from "../contexts/SeasonContext";

// Helper to display division names consistently
const getDivisionDisplayName = (division: string): string => {
  return division === "Women Division" ? "Women Division 1" : division;
};

interface PlayerStatsChange {
  playerId: string;
  playerName: string;
  currentStats: {
    totalPoints: number;
    gamesPlayed: number;
    gamesWon: number;
    gamesLost: number;
    matchesWon: number;
    matchesLost: number;
  };
  newStats: {
    totalPoints: number;
    gamesPlayed: number;
    gamesWon: number;
    gamesLost: number;
    matchesWon: number;
    matchesLost: number;
  };
  hasChanges: boolean;
  changes: {
    totalPoints: number;
    gamesPlayed: number;
    gamesWon: number;
    gamesLost: number;
    matchesWon: number;
    matchesLost: number;
  };
  divisionChanges?: {
    [division: string]: {
      totalPoints: { old: number | string; new: number; change: number };
      gamesPlayed: { old: number | string; new: number; change: number };
      gamesWon: { old: number | string; new: number; change: number };
      gamesLost: { old: number | string; new: number; change: number };
      matchesWon: { old: number | string; new: number; change: number };
      matchesLost: { old: number | string; new: number; change: number };
      leagueDaysPlayed: { old: number | string; new: number; change: number };
    };
  };
  seasonChanges?: {
    [seasonId: string]: {
      status: 'new' | 'updated';
      totalPoints: { old: number; new: number };
      gamesPlayed: { old: number; new: number };
      divisionStats?: {
        [division: string]: {
          status: 'new' | 'updated';
          totalPoints: { old: number; new: number };
          gamesPlayed: { old: number; new: number };
        };
      };
    };
  };
}

interface PreviewData {
  totalPlayers: number;
  playersWithChanges: number;
  playersUnchanged: number;
  changes: PlayerStatsChange[];
}

interface BadgeDetail {
  type: string;
  name: string;
  emoji: string;
  description: string;
  earnedAt: Date;
  metadata?: any;
  pointsAwarded: number;
  division?: string;
}

interface PlayerBadgeResult {
  playerId: string;
  playerName: string;
  division: string;
  currentBadgeCount: number;
  newBadges: BadgeDetail[];
  newBadgeCount: number;
  totalPointsToAward: number;
}


function Dangerous() {
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewData, setPreviewData] = useState<PreviewData | null>(null);
  const [loading, setLoading] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Badge awarding — streaming state
  const [badgeStreamOpen, setBadgeStreamOpen] = useState(false);
  const [badgeStreamTotal, setBadgeStreamTotal] = useState(0);
  const [badgeStreamProcessed, setBadgeStreamProcessed] = useState(0);
  const [badgeStreamResults, setBadgeStreamResults] = useState<PlayerBadgeResult[]>([]);
  const [badgeStreamComplete, setBadgeStreamComplete] = useState(false);
  const [badgeStreamTotalNewBadges, setBadgeStreamTotalNewBadges] = useState(0);
  const [badgeStreamTotalPoints, setBadgeStreamTotalPoints] = useState(0);
  const [badgeStreamPlayersWithBadges, setBadgeStreamPlayersWithBadges] = useState(0);
  const [badgeStreamSeasonId, setBadgeStreamSeasonId] = useState<string | null>(null);
  const [badgeLoading, setBadgeLoading] = useState(false);
  const [badgeExecuting, setBadgeExecuting] = useState(false);
  const badgeCancelledRef = useRef(false);

  // Rank initialization state
  const [rankInitLoading, setRankInitLoading] = useState(false);
  const [cleanupCrossSeasonLoading, setCleanupCrossSeasonLoading] = useState(false);
  const [cleanupGuestDivisionLoading, setCleanupGuestDivisionLoading] = useState(false);
  const [cleanupFirstDayLoading, setCleanupFirstDayLoading] = useState(false);
  const [migrateCleanSweepLoading, setMigrateCleanSweepLoading] = useState(false);
  const [cleanupEarlyBadgesLoading, setCleanupEarlyBadgesLoading] = useState(false);

  // Season filter for badge operations
  const [badgeSeasonId, setBadgeSeasonId] = useState<string>("");
  // Season filter for stats recalculation
  const [statsSeasonId, setStatsSeasonId] = useState<string>("");
  const { allSeasons } = useSeason();

  // Fix all season stats state
  const [seasonStatsPreviewDialogOpen, setSeasonStatsPreviewDialogOpen] = useState(false);
  const [seasonStatsPreviewData, setSeasonStatsPreviewData] = useState<PreviewData | null>(null);
  const [seasonStatsLoading, setSeasonStatsLoading] = useState(false);
  const [seasonStatsExecuting, setSeasonStatsExecuting] = useState(false);

  const handleRecalculateStatsClick = async () => {
    setLoading(true);
    setError("");

    try {
      const params = new URLSearchParams();
      if (statsSeasonId) params.set("seasonId", statsSeasonId);
      const response = await apiRequest(
        `/api/dangerous/recalculate-stats/preview${params.toString() ? `?${params}` : ""}`
      );

      if (response.ok) {
        const data = await response.json();
        setPreviewData(data);
        setPreviewDialogOpen(true);
      } else {
        const errorData = await response.json();
        setError(errorData.error || "Failed to fetch preview data");
      }
    } catch (error) {
      console.error("Error fetching preview:", error);
      setError("Failed to fetch preview data");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmRecalculation = async () => {
    setExecuting(true);
    setError("");

    try {
      const response = await apiRequest(
        "/api/dangerous/recalculate-stats/execute",
        {
          method: "POST",
          body: JSON.stringify({ seasonId: statsSeasonId || undefined }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        setSuccess(
          `Successfully recalculated stats for ${data.playersUpdated} players. ${data.playersWithChanges} players had changes.`
        );
        setPreviewDialogOpen(false);
        setPreviewData(null);
      } else {
        const errorData = await response.json();
        setError(errorData.error || "Failed to execute recalculation");
      }
    } catch (error) {
      console.error("Error executing recalculation:", error);
      setError("Failed to execute recalculation");
    } finally {
      setExecuting(false);
    }
  };

  const formatChange = (value: number) => {
    if (value === 0) return "0";
    return value > 0 ? `+${value}` : `${value}`;
  };

  const handleAwardBadgesClick = async () => {
    // Reset streaming state
    badgeCancelledRef.current = false;
    setBadgeStreamTotal(0);
    setBadgeStreamProcessed(0);
    setBadgeStreamResults([]);
    setBadgeStreamComplete(false);
    setBadgeStreamTotalNewBadges(0);
    setBadgeStreamTotalPoints(0);
    setBadgeStreamPlayersWithBadges(0);
    setBadgeStreamSeasonId(badgeSeasonId || null);
    setBadgeLoading(true);
    setError("");

    try {
      const params = new URLSearchParams();
      if (badgeSeasonId) params.set("seasonId", badgeSeasonId);
      const response = await apiRequest(
        `/api/dangerous/award-missing-badges/stream${params.toString() ? `?${params}` : ""}`
      );

      if (!response.ok || !response.body) {
        const errorData = await response.json().catch(() => ({}));
        setError((errorData as any).error || "Failed to start badge check");
        setBadgeLoading(false);
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done || badgeCancelledRef.current) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() ?? "";

        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          try {
            const event = JSON.parse(line.slice(6));

            if (event.type === "total") {
              setBadgeStreamTotal(event.total);
              setBadgeStreamOpen(true);
              setBadgeLoading(false);
            } else if (event.type === "player") {
              setBadgeStreamProcessed(event.processed);
              const { type: _t, processed: _p, ...playerResult } = event;
              setBadgeStreamResults((prev) => [...prev, playerResult]);
              setBadgeStreamTotalNewBadges((prev) => prev + event.newBadgeCount);
              setBadgeStreamTotalPoints((prev) => prev + event.totalPointsToAward);
              setBadgeStreamPlayersWithBadges((prev) => prev + 1);
            } else if (event.type === "progress") {
              setBadgeStreamProcessed(event.processed);
            } else if (event.type === "done") {
              setBadgeStreamProcessed(event.totalPlayers);
              setBadgeStreamComplete(true);
            } else if (event.type === "error") {
              setError(event.message || "Badge check failed");
            }
          } catch {
            // ignore malformed lines
          }
        }
      }
      reader.releaseLock();
    } catch {
      setError("Failed to connect to badge check");
    } finally {
      setBadgeLoading(false);
    }
  };

  const handleCloseBadgeStream = () => {
    if (!badgeExecuting) {
      badgeCancelledRef.current = true;
      setBadgeStreamOpen(false);
    }
  };

  const handleConfirmBadgeAward = async () => {
    setBadgeExecuting(true);
    setError("");

    try {
      const response = await apiRequest(
        "/api/dangerous/award-missing-badges/execute",
        {
          method: "POST",
          body: JSON.stringify({
            results: badgeStreamResults,
            seasonId: badgeStreamSeasonId,
          }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        setSuccess(
          `Successfully awarded ${data.totalBadgesAwarded} badge(s) to ${data.playersUpdated} players (+${data.totalPointsAwarded ?? 0} bonus points).`
        );
        setBadgeStreamOpen(false);
        setBadgeStreamResults([]);
      } else {
        const errorData = await response.json();
        setError(errorData.error || "Failed to execute badge awards");
      }
    } catch {
      setError("Failed to execute badge awards");
    } finally {
      setBadgeExecuting(false);
    }
  };

  const handleCleanupCrossSeasonBadges = async () => {
    setCleanupCrossSeasonLoading(true);
    setError("");
    setSuccess("");
    try {
      const response = await apiRequest("/api/dangerous/cleanup-cross-season-badges", {
        method: "POST",
      });
      if (response.ok) {
        const data = await response.json();
        if (data.badgesRemoved === 0) {
          setSuccess("No cross-season badges found. Everything is already clean.");
        } else {
          const detail = data.details
            .map((d: any) => `${d.playerName}: ${d.removed.map((r: any) => `${r.type} (was ${r.badgeSeasonId}, actual ${r.actualSeason})`).join(", ")}`)
            .join(" | ");
          setSuccess(`Removed ${data.badgesRemoved} cross-season badge(s) from ${data.playersFixed} player(s). ${detail}`);
        }
      } else {
        const errorData = await response.json();
        setError(errorData.error || "Failed to clean up cross-season badges");
      }
    } catch (error) {
      setError("Failed to clean up cross-season badges");
    } finally {
      setCleanupCrossSeasonLoading(false);
    }
  };

  const handleCleanupGuestDivisionBadges = async () => {
    setCleanupGuestDivisionLoading(true);
    setError("");
    setSuccess("");
    try {
      const response = await apiRequest("/api/dangerous/cleanup-guest-division-badges", {
        method: "POST",
      });
      if (response.ok) {
        const data = await response.json();
        if (data.badgesRemoved === 0) {
          setSuccess("No guest-division badges found. Everything is already clean.");
        } else {
          const detail = data.details
            .map((d: any) => `${d.playerName}: ${d.removed.map((r: any) => `${r.type} (player div: ${r.playerDivision}, match div: ${r.matchDivision})`).join(", ")}`)
            .join(" | ");
          setSuccess(`Removed ${data.badgesRemoved} guest-division badge(s) from ${data.playersFixed} player(s). ${detail}`);
        }
      } else {
        const errorData = await response.json();
        setError(errorData.error || "Failed to clean up guest-division badges");
      }
    } catch (error) {
      setError("Failed to clean up guest-division badges");
    } finally {
      setCleanupGuestDivisionLoading(false);
    }
  };

  const handleCleanupFirstDayBadges = async () => {
    setCleanupFirstDayLoading(true);
    setError("");
    setSuccess("");
    try {
      const response = await apiRequest("/api/dangerous/cleanup-first-day-ranking-badges", {
        method: "POST",
      });
      if (response.ok) {
        const data = await response.json();
        if (data.badgesRemoved === 0) {
          setSuccess("No first-day ranking badges found. Everything is already clean.");
        } else {
          const detail = data.details
            .map((d: any) => `${d.playerName}: ${d.removed.map((r: any) => r.type).join(", ")}`)
            .join(" | ");
          setSuccess(`Removed ${data.badgesRemoved} badge(s) from ${data.playersFixed} player(s). ${detail}`);
        }
      } else {
        const errorData = await response.json();
        setError(errorData.error || "Failed to clean up first-day badges");
      }
    } catch (error) {
      setError("Failed to clean up first-day badges");
    } finally {
      setCleanupFirstDayLoading(false);
    }
  };

  const handleCleanupEarlyBadges = async () => {
    setCleanupEarlyBadgesLoading(true);
    setError("");
    setSuccess("");
    try {
      const response = await apiRequest("/api/dangerous/cleanup-early-ranking-badges", {
        method: "POST",
      });
      if (response.ok) {
        const data = await response.json();
        if (data.badgesRemoved === 0) {
          setSuccess("No early-ranking badges found. Everything is already clean.");
        } else {
          const detail = data.details
            .map((d: any) => `${d.playerName}: ${d.removed.map((r: any) => r.type).join(", ")}`)
            .join(" | ");
          setSuccess(`Removed ${data.badgesRemoved} badge(s) from ${data.playersFixed} player(s). ${detail}`);
        }
      } else {
        const errorData = await response.json();
        setError(errorData.error || "Failed to clean up early-ranking badges");
      }
    } catch (error) {
      setError("Failed to clean up early-ranking badges");
    } finally {
      setCleanupEarlyBadgesLoading(false);
    }
  };

  const handleMigrateCleanSweep = async () => {
    setMigrateCleanSweepLoading(true);
    setError("");
    setSuccess("");
    try {
      const response = await apiRequest("/api/dangerous/migrate-clean-sweep-match-ids", {
        method: "POST",
      });
      if (response.ok) {
        const data = await response.json();
        if (data.badgesUpdated === 0) {
          setSuccess("No legacy Clean Sweep badges found. All are already up to date.");
        } else {
          setSuccess(`Stamped matchId onto ${data.badgesUpdated} Clean Sweep badge(s) across ${data.playersFixed} player(s). You can now safely run Award Missing Badges.`);
        }
      } else {
        const errorData = await response.json();
        setError(errorData.error || "Failed to migrate Clean Sweep badges");
      }
    } catch (error) {
      setError("Failed to migrate Clean Sweep badges");
    } finally {
      setMigrateCleanSweepLoading(false);
    }
  };

  const handleInitializeRanks = async () => {
    setRankInitLoading(true);
    setError("");
    setSuccess("");

    try {
      const response = await apiRequest("/api/dangerous/initialize-ranks", {
        method: "POST",
      });

      if (response.ok) {
        const data = await response.json();
        setSuccess(
          `Successfully initialized ranks for ${data.totalPlayersUpdated} players across ${data.divisionsProcessed} divisions!`
        );
      } else {
        const errorData = await response.json();
        setError(errorData.error || "Failed to initialize ranks");
      }
    } catch (error) {
      console.error("Error initializing ranks:", error);
      setError("Failed to initialize ranks");
    } finally {
      setRankInitLoading(false);
    }
  };

  const handleFixSeasonStatsClick = async () => {
    setSeasonStatsLoading(true);
    setError("");
    try {
      const response = await apiRequest("/api/dangerous/fix-season-stats/preview");
      if (response.ok) {
        const data = await response.json();
        setSeasonStatsPreviewData(data);
        setSeasonStatsPreviewDialogOpen(true);
      } else {
        const errorData = await response.json();
        setError(errorData.error || "Failed to fetch season stats preview");
      }
    } catch {
      setError("Failed to fetch season stats preview");
    } finally {
      setSeasonStatsLoading(false);
    }
  };

  const handleConfirmSeasonStatsFix = async () => {
    setSeasonStatsExecuting(true);
    setError("");
    try {
      const response = await apiRequest("/api/dangerous/fix-season-stats/execute", {
        method: "POST",
      });
      if (response.ok) {
        const data = await response.json();
        setSuccess(
          `Season stats fixed for ${data.playersUpdated} player(s). Root stats were not changed.`
        );
        setSeasonStatsPreviewDialogOpen(false);
        setSeasonStatsPreviewData(null);
      } else {
        const errorData = await response.json();
        setError(errorData.error || "Failed to fix season stats");
      }
    } catch {
      setError("Failed to fix season stats");
    } finally {
      setSeasonStatsExecuting(false);
    }
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mb: 3,
          gap: 2,
        }}
      >
        <WarningIcon sx={{ fontSize: 40, color: "error.main" }} />
        <Box>
          <Typography variant="h4" sx={{ color: "error.main" }}>
            Dangerous Operations
          </Typography>
          <Typography variant="body2" color="text.secondary">
            These operations modify database data in bulk. Use with caution.
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess("")}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Recalculate Player Stats Card */}
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              border: 2,
              borderColor: "warning.main",
              "&:hover": {
                boxShadow: 6,
              },
            }}
          >
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <RefreshIcon
                  sx={{ fontSize: 32, color: "warning.main", mr: 1 }}
                />
                <Typography variant="h6">Recalculate Player Stats</Typography>
              </Box>

              <Typography variant="body2" color="text.secondary" paragraph>
                Recalculates all player statistics from scratch based on actual
                match data. This fixes any discrepancies caused by duplicate
                updates or bugs.
              </Typography>

              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>What this does:</strong>
                </Typography>
                <ul style={{ margin: "8px 0 0 0", paddingLeft: 20 }}>
                  <li>Recalculates total points from completed matches</li>
                  <li>Includes current-season badge bonus points</li>
                  <li>Recalculates games won/lost and matches won/lost</li>
                  <li>Recalculates per-division statistics</li>
                  <li>Applies penalties and bonuses</li>
                  <li>Updates all player documents in the database</li>
                </ul>
              </Alert>

              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>Season (optional — leave blank for all)</InputLabel>
                <Select
                  value={statsSeasonId}
                  label="Season (optional — leave blank for all)"
                  onChange={(e) => setStatsSeasonId(e.target.value)}
                >
                  <MenuItem value="">All seasons</MenuItem>
                  {allSeasons
                    .filter((s) => s.status !== "draft")
                    .map((s) => (
                      <MenuItem key={s.id} value={s.id}>
                        {s.name} {s.isActive ? "(Active)" : ""}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>

              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Button
                  variant="contained"
                  color="warning"
                  startIcon={<RefreshIcon />}
                  onClick={handleRecalculateStatsClick}
                  disabled={loading}
                  fullWidth
                >
                  {loading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    "Preview Changes"
                  )}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Fix All Season Stats Card */}
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              border: 2,
              borderColor: "warning.main",
              "&:hover": { boxShadow: 6 },
            }}
          >
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <Box sx={{ fontSize: 32, mr: 1 }}>📅</Box>
                <Typography variant="h6">Fix All Season Stats</Typography>
              </Box>

              <Typography variant="body2" color="text.secondary" paragraph>
                Recalculates <strong>seasonStats</strong> for every player across all seasons from
                match data. Root-level points and rankings are <strong>not touched</strong> — use
                "Recalculate Player Stats" (all seasons) for those.
              </Typography>

              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Use this when:</strong>
                </Typography>
                <ul style={{ margin: "8px 0 0 0", paddingLeft: 20 }}>
                  <li>A previous recalculation may have written wrong season stats</li>
                  <li>Per-season breakdown on player profiles looks incorrect</li>
                  <li>You want to rebuild season history without touching root points</li>
                </ul>
              </Alert>

              <Button
                variant="contained"
                color="warning"
                startIcon={<RefreshIcon />}
                onClick={handleFixSeasonStatsClick}
                disabled={seasonStatsLoading}
                fullWidth
              >
                {seasonStatsLoading ? (
                  <>
                    <CircularProgress size={24} color="inherit" sx={{ mr: 1 }} />
                    Checking all players...
                  </>
                ) : (
                  "Preview Season Stats Fix"
                )}
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Award Missing Badges Card */}
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              border: 2,
              borderColor: "warning.main",
              "&:hover": {
                boxShadow: 6,
              },
            }}
          >
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <Box sx={{ fontSize: 32, mr: 1 }}>🏆</Box>
                <Typography variant="h6">Award Missing Badges</Typography>
              </Box>

              <Typography variant="body2" color="text.secondary" paragraph>
                Checks all players and awards any badges they have earned but
                haven't received yet. Excludes Test Division players.
              </Typography>

              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>What this does:</strong>
                </Typography>
                <ul style={{ margin: "8px 0 0 0", paddingLeft: 20 }}>
                  <li>Checks all players for missing achievement badges</li>
                  <li>Awards badges for Perfect Day, Giant Killer, etc.</li>
                  <li>Credits bonus points for each badge awarded</li>
                  <li>Excludes Test Division players from processing</li>
                  <li>Shows live progress of each player checked</li>
                  <li>Updates player documents with new badges</li>
                </ul>
              </Alert>

              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>Season (optional)</InputLabel>
                <Select
                  value={badgeSeasonId}
                  label="Season (optional)"
                  onChange={(e) => setBadgeSeasonId(e.target.value)}
                >
                  <MenuItem value="">All seasons</MenuItem>
                  {allSeasons
                    .filter((s) => s.status !== "draft")
                    .map((s) => (
                      <MenuItem key={s.id} value={s.id}>
                        {s.name} {s.isActive ? "(Active)" : ""}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>

              <Button
                variant="contained"
                color="warning"
                startIcon={<Box>🏆</Box>}
                onClick={handleAwardBadgesClick}
                disabled={badgeLoading}
                fullWidth
              >
                {badgeLoading ? (
                  <>
                    <CircularProgress size={24} color="inherit" sx={{ mr: 1 }} />
                    Connecting...
                  </>
                ) : (
                  "Preview Badge Awards"
                )}
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Cleanup Guest-Division Badges Card */}
        <Grid item xs={12} md={6}>
          <Card sx={{ border: 2, borderColor: "error.main", "&:hover": { boxShadow: 6 } }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <Box sx={{ fontSize: 32, mr: 1 }}>🚫</Box>
                <Typography variant="h6">Clean Up Guest-Division Badges</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" paragraph>
                Removes badges earned by players while <strong>helping out in another division</strong> (not their registered division). Checks each badge's league day division against the player's registered division for that season.
              </Typography>
              <Alert severity="warning" sx={{ mb: 2 }}>
                After running this, re-run <strong>Award Missing Badges</strong> with the relevant season selected to re-award any badges the player legitimately earned in their own division.
              </Alert>
              <Button
                variant="contained"
                color="error"
                fullWidth
                onClick={handleCleanupGuestDivisionBadges}
                disabled={cleanupGuestDivisionLoading}
              >
                {cleanupGuestDivisionLoading ? (
                  <>
                    <CircularProgress size={24} color="inherit" sx={{ mr: 1 }} />
                    Cleaning up...
                  </>
                ) : (
                  "Clean Up Guest-Division Badges"
                )}
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Cleanup First-Day Ranking Badges Card */}
        <Grid item xs={12} md={6}>
          <Card sx={{ border: 2, borderColor: "error.main", "&:hover": { boxShadow: 6 } }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <Box sx={{ fontSize: 32, mr: 1 }}>1️⃣</Box>
                <Typography variant="h6">Clean Up First-Day Ranking Badges</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" paragraph>
                Removes <strong>Giant Killer</strong> and <strong>Partner Carry</strong> badges earned on the <em>first league day of a season</em>, when rankings are arbitrary. Going forward these badges are blocked from being awarded on day one.
              </Typography>
              <Alert severity="warning" sx={{ mb: 2 }}>
                After running this, re-run <strong>Award Missing Badges</strong> to re-award any that were legitimately earned from the second league day onward.
              </Alert>
              <Button
                variant="contained"
                color="error"
                fullWidth
                onClick={handleCleanupFirstDayBadges}
                disabled={cleanupFirstDayLoading}
              >
                {cleanupFirstDayLoading ? (
                  <>
                    <CircularProgress size={24} color="inherit" sx={{ mr: 1 }} />
                    Cleaning up...
                  </>
                ) : (
                  "Clean Up First-Day Ranking Badges"
                )}
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Cleanup Early-Ranking Badges (first 4 league days) Card */}
        <Grid item xs={12} md={6}>
          <Card sx={{ border: 2, borderColor: "error.main", "&:hover": { boxShadow: 6 } }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <Box sx={{ fontSize: 32, mr: 1 }}>4️⃣</Box>
                <Typography variant="h6">Clean Up Early-Season Ranking Badges</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" paragraph>
                Removes <strong>Giant Killer</strong> and <strong>Partner Carry</strong> badges earned
                during the <em>first 4 league days</em> of a season, before rankings had a chance to
                stabilise. Going forward these badges are blocked until league day 5.
              </Typography>
              <Alert severity="warning" sx={{ mb: 2 }}>
                After running this, re-run <strong>Award Missing Badges</strong> with the relevant
                season selected to re-award any badges legitimately earned from league day 5 onward.
              </Alert>
              <Button
                variant="contained"
                color="error"
                fullWidth
                onClick={handleCleanupEarlyBadges}
                disabled={cleanupEarlyBadgesLoading}
              >
                {cleanupEarlyBadgesLoading ? (
                  <>
                    <CircularProgress size={24} color="inherit" sx={{ mr: 1 }} />
                    Cleaning up...
                  </>
                ) : (
                  "Clean Up Early-Season Ranking Badges"
                )}
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Migrate Clean Sweep Match IDs Card */}
        <Grid item xs={12} md={6}>
          <Card sx={{ border: 2, borderColor: "warning.main", "&:hover": { boxShadow: 6 } }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <Box sx={{ fontSize: 32, mr: 1 }}>🧹</Box>
                <Typography variant="h6">Migrate Clean Sweep — Stamp Match IDs</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" paragraph>
                One-time migration. Old <strong>Clean Sweep</strong> badges were stored with only a <code>leagueDayId</code>. The new logic deduplicates by <code>matchId</code>. Run this <em>before</em> Award Missing Badges to prevent players getting duplicate badges for wins they already earned.
              </Typography>
              <Alert severity="warning" sx={{ mb: 2 }}>
                Run this <strong>once</strong> after deploying the Clean Sweep matchId change. Safe to re-run — already-stamped badges are skipped.
              </Alert>
              <Button
                variant="contained"
                color="warning"
                fullWidth
                onClick={handleMigrateCleanSweep}
                disabled={migrateCleanSweepLoading}
              >
                {migrateCleanSweepLoading ? (
                  <>
                    <CircularProgress size={24} color="inherit" sx={{ mr: 1 }} />
                    Migrating...
                  </>
                ) : (
                  "Migrate Clean Sweep Match IDs"
                )}
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Cleanup Cross-Season Badges Card */}
        <Grid item xs={12} md={6}>
          <Card sx={{ border: 2, borderColor: "error.main", "&:hover": { boxShadow: 6 } }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <Box sx={{ fontSize: 32, mr: 1 }}>🧹</Box>
                <Typography variant="h6">Clean Up Cross-Season Badges</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" paragraph>
                Removes badges that were incorrectly stamped with the wrong <code>seasonId</code> — e.g. a Clean Sweep from January (Winter 25/26) that got tagged as Summer 26. Checks each badge's <code>leagueDayId</code> against the actual league day season.
              </Typography>
              <Alert severity="warning" sx={{ mb: 2 }}>
                After running this, re-run <strong>Award Missing Badges</strong> with Summer 26 selected to re-award any badges that genuinely belong to this season.
              </Alert>
              <Button
                variant="contained"
                color="error"
                fullWidth
                onClick={handleCleanupCrossSeasonBadges}
                disabled={cleanupCrossSeasonLoading}
              >
                {cleanupCrossSeasonLoading ? (
                  <>
                    <CircularProgress size={24} color="inherit" sx={{ mr: 1 }} />
                    Cleaning up...
                  </>
                ) : (
                  "Clean Up Cross-Season Badges"
                )}
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Initialize Rank Tracking Card */}
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              border: 2,
              borderColor: "info.main",
              "&:hover": {
                boxShadow: 6,
              },
            }}
          >
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <Box sx={{ fontSize: 32, mr: 1 }}>📊</Box>
                <Typography variant="h6">Initialize Rank Tracking</Typography>
              </Box>

              <Typography variant="body2" color="text.secondary" paragraph>
                One-time setup: Initialize previous ranks for all players so rank
                movement indicators (⬆️⬇️) will appear starting from the next
                league day.
              </Typography>

              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>What this does:</strong>
                </Typography>
                <ul style={{ margin: "8px 0 0 0", paddingLeft: 20 }}>
                  <li>Calculates current rank for each player in each division</li>
                  <li>Sets current rank as "previous rank" for comparison</li>
                  <li>Enables rank change arrows (⬆️⬇️) in rankings</li>
                  <li>Only needs to be run once</li>
                  <li>Safe to run multiple times (will reset to current ranks)</li>
                </ul>
              </Alert>

              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Button
                  variant="contained"
                  color="info"
                  startIcon={<Box>📊</Box>}
                  onClick={handleInitializeRanks}
                  disabled={rankInitLoading}
                  fullWidth
                >
                  {rankInitLoading ? (
                    <>
                      <CircularProgress size={24} color="inherit" sx={{ mr: 1 }} />
                      Initializing...
                    </>
                  ) : (
                    "Initialize Ranks"
                  )}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Preview Dialog */}
      <Dialog
        open={previewDialogOpen}
        onClose={() => !executing && setPreviewDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <WarningIcon color="warning" />
          Preview: Player Stats Recalculation
        </DialogTitle>
        <DialogContent>
          {previewData && (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Summary:</strong>
                </Typography>
                <Typography variant="body2">
                  Total Players: {previewData.totalPlayers}
                </Typography>
                <Typography variant="body2">
                  Players with changes: {previewData.playersWithChanges}
                </Typography>
                <Typography variant="body2">
                  Players unchanged: {previewData.playersUnchanged}
                </Typography>
              </Alert>

              {previewData.playersWithChanges > 0 ? (
                <>
                  <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                    Players with Changes ({previewData.playersWithChanges})
                  </Typography>

                  <Box sx={{ maxHeight: 500, overflow: "auto" }}>
                    {previewData.changes.map((change, index) => (
                      <Accordion key={change.playerId}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                              width: "100%",
                            }}
                          >
                            <Typography sx={{ fontWeight: "bold" }}>
                              {change.playerName}
                            </Typography>
                            <Chip
                              label={`Total Points: ${formatChange(
                                change.changes.totalPoints
                              )}`}
                              color={
                                change.changes.totalPoints > 0
                                  ? "success"
                                  : change.changes.totalPoints < 0
                                  ? "error"
                                  : "default"
                              }
                              size="small"
                            />
                            {(change as any).badgePtsApplied > 0 && (
                              <Chip
                                label={`🎯 includes +${(change as any).badgePtsApplied} badge pts`}
                                color="warning"
                                size="small"
                                variant="outlined"
                              />
                            )}
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                          <TableContainer>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Stat</TableCell>
                                  <TableCell align="right">Current</TableCell>
                                  <TableCell align="center">→</TableCell>
                                  <TableCell align="right">New</TableCell>
                                  <TableCell align="right">Change</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                <TableRow>
                                  <TableCell>Total Points</TableCell>
                                  <TableCell align="right">
                                    {change.currentStats.totalPoints}
                                  </TableCell>
                                  <TableCell align="center">→</TableCell>
                                  <TableCell align="right">
                                    {change.newStats.totalPoints}
                                  </TableCell>
                                  <TableCell
                                    align="right"
                                    sx={{
                                      color:
                                        change.changes.totalPoints > 0
                                          ? "success.main"
                                          : change.changes.totalPoints < 0
                                          ? "error.main"
                                          : "inherit",
                                      fontWeight: "bold",
                                    }}
                                  >
                                    {formatChange(change.changes.totalPoints)}
                                  </TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell>Games Played</TableCell>
                                  <TableCell align="right">
                                    {change.currentStats.gamesPlayed}
                                  </TableCell>
                                  <TableCell align="center">→</TableCell>
                                  <TableCell align="right">
                                    {change.newStats.gamesPlayed}
                                  </TableCell>
                                  <TableCell align="right">
                                    {formatChange(change.changes.gamesPlayed)}
                                  </TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell>Games Won</TableCell>
                                  <TableCell align="right">
                                    {change.currentStats.gamesWon}
                                  </TableCell>
                                  <TableCell align="center">→</TableCell>
                                  <TableCell align="right">
                                    {change.newStats.gamesWon}
                                  </TableCell>
                                  <TableCell align="right">
                                    {formatChange(change.changes.gamesWon)}
                                  </TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell>Games Lost</TableCell>
                                  <TableCell align="right">
                                    {change.currentStats.gamesLost}
                                  </TableCell>
                                  <TableCell align="center">→</TableCell>
                                  <TableCell align="right">
                                    {change.newStats.gamesLost}
                                  </TableCell>
                                  <TableCell align="right">
                                    {formatChange(change.changes.gamesLost)}
                                  </TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell>Matches Won</TableCell>
                                  <TableCell align="right">
                                    {change.currentStats.matchesWon}
                                  </TableCell>
                                  <TableCell align="center">→</TableCell>
                                  <TableCell align="right">
                                    {change.newStats.matchesWon}
                                  </TableCell>
                                  <TableCell align="right">
                                    {formatChange(change.changes.matchesWon)}
                                  </TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell>Matches Lost</TableCell>
                                  <TableCell align="right">
                                    {change.currentStats.matchesLost}
                                  </TableCell>
                                  <TableCell align="center">→</TableCell>
                                  <TableCell align="right">
                                    {change.newStats.matchesLost}
                                  </TableCell>
                                  <TableCell align="right">
                                    {formatChange(change.changes.matchesLost)}
                                  </TableCell>
                                </TableRow>
                              </TableBody>
                            </Table>
                          </TableContainer>

                          {/* Division-level changes */}
                          {change.divisionChanges &&
                            Object.keys(change.divisionChanges).length > 0 && (
                              <Box sx={{ mt: 2 }}>
                                <Typography
                                  variant="subtitle2"
                                  sx={{ mb: 1, fontWeight: "bold" }}
                                >
                                  Division Stats Changes:
                                </Typography>
                                {Object.entries(change.divisionChanges).map(
                                  ([division, divChanges]: [string, any]) => (
                                    <Box
                                      key={division}
                                      sx={{
                                        mb: 2,
                                        pl: 2,
                                        borderLeft: "3px solid #1976d2",
                                      }}
                                    >
                                      <Typography
                                        variant="body2"
                                        sx={{ fontWeight: "bold", mb: 1 }}
                                      >
                                        {getDivisionDisplayName(division)}
                                      </Typography>
                                      <TableContainer>
                                        <Table size="small">
                                          <TableBody>
                                            <TableRow>
                                              <TableCell>
                                                Total Points
                                              </TableCell>
                                              <TableCell align="right">
                                                {divChanges.totalPoints.old}
                                              </TableCell>
                                              <TableCell align="center">
                                                →
                                              </TableCell>
                                              <TableCell align="right">
                                                {divChanges.totalPoints.new}
                                              </TableCell>
                                              <TableCell
                                                align="right"
                                                sx={{
                                                  color:
                                                    divChanges.totalPoints
                                                      .change > 0
                                                      ? "success.main"
                                                      : divChanges.totalPoints
                                                          .change < 0
                                                      ? "error.main"
                                                      : "inherit",
                                                  fontWeight: "bold",
                                                }}
                                              >
                                                {formatChange(
                                                  divChanges.totalPoints.change
                                                )}
                                              </TableCell>
                                            </TableRow>
                                            <TableRow>
                                              <TableCell>
                                                Games Played
                                              </TableCell>
                                              <TableCell align="right">
                                                {divChanges.gamesPlayed.old}
                                              </TableCell>
                                              <TableCell align="center">
                                                →
                                              </TableCell>
                                              <TableCell align="right">
                                                {divChanges.gamesPlayed.new}
                                              </TableCell>
                                              <TableCell align="right">
                                                {formatChange(
                                                  divChanges.gamesPlayed.change
                                                )}
                                              </TableCell>
                                            </TableRow>
                                            <TableRow>
                                              <TableCell>Games Won</TableCell>
                                              <TableCell align="right">
                                                {divChanges.gamesWon.old}
                                              </TableCell>
                                              <TableCell align="center">
                                                →
                                              </TableCell>
                                              <TableCell align="right">
                                                {divChanges.gamesWon.new}
                                              </TableCell>
                                              <TableCell align="right">
                                                {formatChange(
                                                  divChanges.gamesWon.change
                                                )}
                                              </TableCell>
                                            </TableRow>
                                            <TableRow>
                                              <TableCell>Games Lost</TableCell>
                                              <TableCell align="right">
                                                {divChanges.gamesLost.old}
                                              </TableCell>
                                              <TableCell align="center">
                                                →
                                              </TableCell>
                                              <TableCell align="right">
                                                {divChanges.gamesLost.new}
                                              </TableCell>
                                              <TableCell align="right">
                                                {formatChange(
                                                  divChanges.gamesLost.change
                                                )}
                                              </TableCell>
                                            </TableRow>
                                            <TableRow>
                                              <TableCell>Matches Won</TableCell>
                                              <TableCell align="right">
                                                {divChanges.matchesWon.old}
                                              </TableCell>
                                              <TableCell align="center">
                                                →
                                              </TableCell>
                                              <TableCell align="right">
                                                {divChanges.matchesWon.new}
                                              </TableCell>
                                              <TableCell align="right">
                                                {formatChange(
                                                  divChanges.matchesWon.change
                                                )}
                                              </TableCell>
                                            </TableRow>
                                            <TableRow>
                                              <TableCell>
                                                Matches Lost
                                              </TableCell>
                                              <TableCell align="right">
                                                {divChanges.matchesLost.old}
                                              </TableCell>
                                              <TableCell align="center">
                                                →
                                              </TableCell>
                                              <TableCell align="right">
                                                {divChanges.matchesLost.new}
                                              </TableCell>
                                              <TableCell align="right">
                                                {formatChange(
                                                  divChanges.matchesLost.change
                                                )}
                                              </TableCell>
                                            </TableRow>
                                            <TableRow>
                                              <TableCell>
                                                League Days Played
                                              </TableCell>
                                              <TableCell align="right">
                                                {
                                                  divChanges.leagueDaysPlayed
                                                    .old
                                                }
                                              </TableCell>
                                              <TableCell align="center">
                                                →
                                              </TableCell>
                                              <TableCell align="right">
                                                {
                                                  divChanges.leagueDaysPlayed
                                                    .new
                                                }
                                              </TableCell>
                                              <TableCell
                                                align="right"
                                                sx={{
                                                  color:
                                                    divChanges.leagueDaysPlayed
                                                      .change !== 0
                                                      ? "info.main"
                                                      : "inherit",
                                                  fontWeight:
                                                    divChanges.leagueDaysPlayed
                                                      .change !== 0
                                                      ? "bold"
                                                      : "normal",
                                                }}
                                              >
                                                {formatChange(
                                                  divChanges.leagueDaysPlayed
                                                    .change
                                                )}
                                              </TableCell>
                                            </TableRow>
                                          </TableBody>
                                        </Table>
                                      </TableContainer>
                                    </Box>
                                  )
                                )}
                              </Box>
                            )}

                          {/* Season-level changes */}
                          {change.seasonChanges &&
                            Object.keys(change.seasonChanges).length > 0 && (
                              <Box sx={{ mt: 2 }}>
                                <Typography
                                  variant="subtitle2"
                                  sx={{ mb: 1, fontWeight: "bold" }}
                                >
                                  Season Stats Changes:
                                </Typography>
                                {Object.entries(change.seasonChanges).map(
                                  ([seasonId, seasonChanges]: [string, any]) => (
                                    <Box
                                      key={seasonId}
                                      sx={{
                                        mb: 2,
                                        pl: 2,
                                        borderLeft: "3px solid #4caf50",
                                      }}
                                    >
                                      <Typography
                                        variant="body2"
                                        sx={{ fontWeight: "bold", mb: 1 }}
                                      >
                                        {seasonId} ({seasonChanges.status === 'new' ? '🆕 New' : '🔄 Updated'}) - Season Totals
                                      </Typography>
                                      <TableContainer>
                                        <Table size="small">
                                          <TableBody>
                                            <TableRow>
                                              <TableCell>
                                                Total Points (All Divisions)
                                              </TableCell>
                                              <TableCell align="right">
                                                {seasonChanges.totalPoints.old}
                                              </TableCell>
                                              <TableCell align="center">
                                                →
                                              </TableCell>
                                              <TableCell align="right">
                                                {seasonChanges.totalPoints.new}
                                              </TableCell>
                                            </TableRow>
                                            <TableRow>
                                              <TableCell>
                                                Games Played (All Divisions)
                                              </TableCell>
                                              <TableCell align="right">
                                                {seasonChanges.gamesPlayed.old}
                                              </TableCell>
                                              <TableCell align="center">
                                                →
                                              </TableCell>
                                              <TableCell align="right">
                                                {seasonChanges.gamesPlayed.new}
                                              </TableCell>
                                            </TableRow>
                                          </TableBody>
                                        </Table>
                                      </TableContainer>
                                      
                                      {/* Division breakdown within this season */}
                                      {seasonChanges.divisionStats &&
                                        Object.keys(seasonChanges.divisionStats).length > 0 && (
                                          <Box sx={{ mt: 2, ml: 2 }}>
                                            <Typography
                                              variant="body2"
                                              sx={{ fontWeight: "bold", mb: 1, color: "#666" }}
                                            >
                                              Division Breakdown:
                                            </Typography>
                                            {Object.entries(seasonChanges.divisionStats).map(
                                              ([division, divStats]: [string, any]) => (
                                                <Box
                                                  key={division}
                                                  sx={{
                                                    mb: 1.5,
                                                    pl: 1.5,
                                                    borderLeft: "2px solid #81c784",
                                                  }}
                                                >
                                                  <Typography
                                                    variant="caption"
                                                    sx={{ fontWeight: "bold", display: "block", mb: 0.5 }}
                                                  >
                                                    {division} ({divStats.status === 'new' ? '🆕 New' : '🔄 Updated'})
                                                  </Typography>
                                                  <TableContainer>
                                                    <Table size="small">
                                                      <TableBody>
                                                        <TableRow>
                                                          <TableCell sx={{ fontSize: '0.8rem' }}>
                                                            Points
                                                          </TableCell>
                                                          <TableCell align="right" sx={{ fontSize: '0.8rem' }}>
                                                            {divStats.totalPoints.old}
                                                          </TableCell>
                                                          <TableCell align="center" sx={{ fontSize: '0.8rem' }}>
                                                            →
                                                          </TableCell>
                                                          <TableCell align="right" sx={{ fontSize: '0.8rem' }}>
                                                            {divStats.totalPoints.new}
                                                            {(change as any).badgePtsApplied > 0 && (
                                                              <Typography component="span" variant="caption" color="warning.main" sx={{ ml: 0.5, fontWeight: 'bold' }}>
                                                                (+{(change as any).badgePtsApplied} badge pts)
                                                              </Typography>
                                                            )}
                                                          </TableCell>
                                                        </TableRow>
                                                        <TableRow>
                                                          <TableCell sx={{ fontSize: '0.8rem' }}>
                                                            Games
                                                          </TableCell>
                                                          <TableCell align="right" sx={{ fontSize: '0.8rem' }}>
                                                            {divStats.gamesPlayed.old}
                                                          </TableCell>
                                                          <TableCell align="center" sx={{ fontSize: '0.8rem' }}>
                                                            →
                                                          </TableCell>
                                                          <TableCell align="right" sx={{ fontSize: '0.8rem' }}>
                                                            {divStats.gamesPlayed.new}
                                                          </TableCell>
                                                        </TableRow>
                                                      </TableBody>
                                                    </Table>
                                                  </TableContainer>
                                                </Box>
                                              )
                                            )}
                                          </Box>
                                        )}
                                    </Box>
                                  )
                                )}
                              </Box>
                            )}
                        </AccordionDetails>
                      </Accordion>
                    ))}
                  </Box>
                </>
              ) : (
                <Alert severity="success" sx={{ mt: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CheckIcon />
                    <Typography>
                      All player stats are already correct! No changes needed.
                    </Typography>
                  </Box>
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setPreviewDialogOpen(false)}
            disabled={executing}
            startIcon={<CloseIcon />}
          >
            Cancel
          </Button>
          {previewData && previewData.playersWithChanges > 0 && (
            <Button
              onClick={handleConfirmRecalculation}
              variant="contained"
              color="warning"
              disabled={executing}
              startIcon={
                executing ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <CheckIcon />
                )
              }
            >
              {executing
                ? "Executing..."
                : `Confirm & Execute (${previewData.playersWithChanges} players)`}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Badge Award Streaming Dialog */}
      <Dialog
        open={badgeStreamOpen}
        onClose={handleCloseBadgeStream}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box sx={{ fontSize: 32 }}>🏆</Box>
          Missing Badge Awards
        </DialogTitle>
        <DialogContent>
          {/* Progress bar — visible while scanning */}
          {!badgeStreamComplete && (
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                <Typography variant="body2" color="text.secondary">
                  Checking players…
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {badgeStreamProcessed} / {badgeStreamTotal}
                </Typography>
              </Box>
              <LinearProgress
                variant={badgeStreamTotal > 0 ? "determinate" : "indeterminate"}
                value={badgeStreamTotal > 0 ? (badgeStreamProcessed / badgeStreamTotal) * 100 : undefined}
              />
            </Box>
          )}

          {/* Running summary */}
          <Alert severity={badgeStreamComplete ? "info" : "info"} sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>{badgeStreamComplete ? "Done." : "Scanning…"}</strong>
              {" "}Checked {badgeStreamProcessed} of {badgeStreamTotal} players
              {badgeStreamSeasonId && ` (season: ${badgeStreamSeasonId})`}.
            </Typography>
            {badgeStreamPlayersWithBadges > 0 && (
              <>
                <Typography variant="body2">
                  Players with new badges: <strong>{badgeStreamPlayersWithBadges}</strong>
                </Typography>
                <Typography variant="body2">
                  Total new badges: <strong>{badgeStreamTotalNewBadges}</strong>
                </Typography>
                <Typography variant="body2" sx={{ color: "success.main", fontWeight: "bold" }}>
                  Total bonus points to credit: +{badgeStreamTotalPoints}
                </Typography>
              </>
            )}
          </Alert>

          {/* Results list — grows as players arrive */}
          {badgeStreamResults.length > 0 && (
            <>
              <Typography variant="h6" gutterBottom>
                Players with New Badges ({badgeStreamPlayersWithBadges}
                {!badgeStreamComplete && "…"})
              </Typography>
              <Box sx={{ maxHeight: 460, overflow: "auto" }}>
                {badgeStreamResults.map((result) => (
                  <Accordion key={result.playerId} disableGutters>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, width: "100%", flexWrap: "wrap" }}>
                        <Typography sx={{ fontWeight: "bold" }}>
                          {result.playerName}
                        </Typography>
                        <Chip label={result.division} color="primary" size="small" />
                        <Chip
                          label={`${result.newBadgeCount} new badge${result.newBadgeCount > 1 ? "s" : ""}`}
                          color="success"
                          size="small"
                        />
                        {result.totalPointsToAward > 0 && (
                          <Chip label={`+${result.totalPointsToAward} pts`} color="warning" size="small" />
                        )}
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                        {result.newBadges.map((badge: any, idx: number) => (
                          <Box
                            key={idx}
                            sx={{
                              p: 2,
                              border: "1px solid",
                              borderColor: "divider",
                              borderRadius: 1,
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                            }}
                          >
                            <Typography sx={{ fontSize: 32 }}>{badge.emoji}</Typography>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                                {badge.name}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {badge.description}
                              </Typography>
                              {badge.pointsAwarded > 0 && (
                                <Chip label={`+${badge.pointsAwarded} pts`} color="warning" size="small" sx={{ mt: 0.5 }} />
                              )}
                              {badge.metadata && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                                  {JSON.stringify(badge.metadata)}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            </>
          )}

          {/* No badges found — only show when scan is complete */}
          {badgeStreamComplete && badgeStreamResults.length === 0 && (
            <Alert severity="success" sx={{ mt: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CheckIcon />
                <Typography>All players have already earned all available badges. Nothing to award.</Typography>
              </Box>
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseBadgeStream} disabled={badgeExecuting} startIcon={<CloseIcon />}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmBadgeAward}
            variant="contained"
            color="warning"
            disabled={!badgeStreamComplete || badgeStreamResults.length === 0 || badgeExecuting}
            startIcon={badgeExecuting ? <CircularProgress size={20} color="inherit" /> : <CheckIcon />}
          >
            {badgeExecuting
              ? "Awarding Badges…"
              : !badgeStreamComplete
              ? "Scanning…"
              : `Confirm & Award (${badgeStreamTotalNewBadges} badge${badgeStreamTotalNewBadges !== 1 ? "s" : ""})`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Fix Season Stats Preview Dialog */}
      <Dialog
        open={seasonStatsPreviewDialogOpen}
        onClose={() => !seasonStatsExecuting && setSeasonStatsPreviewDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box sx={{ fontSize: 24 }}>📅</Box>
          Preview: Fix All Season Stats
        </DialogTitle>
        <DialogContent>
          {seasonStatsPreviewData && (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Summary:</strong> {seasonStatsPreviewData.playersWithChanges} player(s)
                  have season stats that differ from match data.
                  Root-level points will <strong>not</strong> be changed.
                </Typography>
              </Alert>

              {seasonStatsPreviewData.playersWithChanges > 0 ? (
                <>
                  <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                    Players with Season Stats Changes ({seasonStatsPreviewData.playersWithChanges})
                  </Typography>
                  <Box sx={{ maxHeight: 500, overflow: "auto" }}>
                    {seasonStatsPreviewData.changes.map((change) => (
                      <Accordion key={change.playerId}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Typography sx={{ fontWeight: "bold" }}>
                            {change.playerName}
                          </Typography>
                          <Chip
                            label={`${Object.keys(change.seasonChanges || {}).length} season(s) affected`}
                            color="warning"
                            size="small"
                            sx={{ ml: 2 }}
                          />
                        </AccordionSummary>
                        <AccordionDetails>
                          {change.seasonChanges &&
                            Object.entries(change.seasonChanges).map(
                              ([seasonId, seasonChanges]: [string, any]) => (
                                <Box
                                  key={seasonId}
                                  sx={{ mb: 2, pl: 2, borderLeft: "3px solid #4caf50" }}
                                >
                                  <Typography variant="body2" sx={{ fontWeight: "bold", mb: 1 }}>
                                    {seasonId} ({seasonChanges.status === "new" ? "New" : "Updated"})
                                  </Typography>
                                  <TableContainer>
                                    <Table size="small">
                                      <TableHead>
                                        <TableRow>
                                          <TableCell>Stat</TableCell>
                                          <TableCell align="right">Current</TableCell>
                                          <TableCell align="center">→</TableCell>
                                          <TableCell align="right">New</TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        <TableRow>
                                          <TableCell>Total Points</TableCell>
                                          <TableCell align="right">{seasonChanges.totalPoints.old}</TableCell>
                                          <TableCell align="center">→</TableCell>
                                          <TableCell align="right">{seasonChanges.totalPoints.new}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                          <TableCell>Games Played</TableCell>
                                          <TableCell align="right">{seasonChanges.gamesPlayed.old}</TableCell>
                                          <TableCell align="center">→</TableCell>
                                          <TableCell align="right">{seasonChanges.gamesPlayed.new}</TableCell>
                                        </TableRow>
                                        {seasonChanges.gamesWon && (
                                          <TableRow>
                                            <TableCell>Games Won</TableCell>
                                            <TableCell align="right">{seasonChanges.gamesWon.old}</TableCell>
                                            <TableCell align="center">→</TableCell>
                                            <TableCell align="right">{seasonChanges.gamesWon.new}</TableCell>
                                          </TableRow>
                                        )}
                                        {seasonChanges.matchesWon && (
                                          <TableRow>
                                            <TableCell>Matches Won</TableCell>
                                            <TableCell align="right">{seasonChanges.matchesWon.old}</TableCell>
                                            <TableCell align="center">→</TableCell>
                                            <TableCell align="right">{seasonChanges.matchesWon.new}</TableCell>
                                          </TableRow>
                                        )}
                                      </TableBody>
                                    </Table>
                                  </TableContainer>

                                  {seasonChanges.divisionStats &&
                                    Object.keys(seasonChanges.divisionStats).length > 0 && (
                                      <Box sx={{ mt: 1.5, ml: 2 }}>
                                        <Typography variant="caption" sx={{ fontWeight: "bold", color: "#666" }}>
                                          Division Breakdown:
                                        </Typography>
                                        {Object.entries(seasonChanges.divisionStats).map(
                                          ([division, divStats]: [string, any]) => (
                                            <Box
                                              key={division}
                                              sx={{ mt: 0.5, pl: 1.5, borderLeft: "2px solid #81c784" }}
                                            >
                                              <Typography variant="caption" sx={{ fontWeight: "bold", display: "block" }}>
                                                {getDivisionDisplayName(division)} ({divStats.status === "new" ? "New" : "Updated"})
                                              </Typography>
                                              <Typography variant="caption" color="text.secondary">
                                                Points: {divStats.totalPoints.old} → {divStats.totalPoints.new} &nbsp;|&nbsp;
                                                Games: {divStats.gamesPlayed.old} → {divStats.gamesPlayed.new}
                                              </Typography>
                                            </Box>
                                          )
                                        )}
                                      </Box>
                                    )}
                                </Box>
                              )
                            )}
                        </AccordionDetails>
                      </Accordion>
                    ))}
                  </Box>
                </>
              ) : (
                <Alert severity="success" sx={{ mt: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CheckIcon />
                    <Typography>All season stats are already correct. No changes needed.</Typography>
                  </Box>
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setSeasonStatsPreviewDialogOpen(false)}
            disabled={seasonStatsExecuting}
            startIcon={<CloseIcon />}
          >
            Cancel
          </Button>
          {seasonStatsPreviewData && seasonStatsPreviewData.playersWithChanges > 0 && (
            <Button
              onClick={handleConfirmSeasonStatsFix}
              variant="contained"
              color="warning"
              disabled={seasonStatsExecuting}
              startIcon={
                seasonStatsExecuting ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <CheckIcon />
                )
              }
            >
              {seasonStatsExecuting
                ? "Fixing..."
                : `Confirm & Fix (${seasonStatsPreviewData.playersWithChanges} players)`}
            </Button>
          )}
        </DialogActions>
      </Dialog>

    </Box>
  );
}

export default Dangerous;

